import {
    ICurrentSubAuths,
    ISubmissionHomePayload
} from "../../../../DataClasses/SubmissionData";
import {
    Card,
    CardContent,
    Typography,
    Grid2 as Grid,
    Divider,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Fade
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { Link } from "react-router-dom";
import SharedMaterialReactTable, { ISharedTableColumn } from "../../../Shared/MaterialReactTable";

export function renderAuditorsTable(data, auditorListingColumns) {
    // Convert column format to SharedMaterialReactTable format
    const getTableColumns = (): ISharedTableColumn<any>[] => {
        return auditorListingColumns.map((col, idx) => ({
            accessorKey: col.name || idx.toString(),
            header: col.label,
            enableSorting: col.options?.sort ?? true,
            enableFiltering: col.options?.filter ?? false,
        }));
    };

    return (
        <Fade in={true}>
            <div>
                <SharedMaterialReactTable
                    data={data}
                    columns={getTableColumns()}
                    title="Auditors"
                    isLoading={false}
                    enablePagination={true}
                    enableSorting={true}
                    enableFiltering={true}
                    enableColumnActions={true}
                    enableRowActions={false}
                    initialState={{
                        pagination: { pageIndex: 0, pageSize: 20 },
                    }}
                    muiPaginationProps={{
                        rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                    }}
                    emptyMessage="No auditors found."
                />
            </div>
        </Fade>
    );
}

export function renderSubmissionSummary(
    auths: ICurrentSubAuths,
    payload: ISubmissionHomePayload,
    classes
) {
    return (
        <Card className={classes.card}>
            <CardContent>
                <Typography className={classes.primaryBlue}>
                    Submission Details
                </Typography>
                <Divider></Divider>
                <Table size="small" aria-label="simple table">
                    <TableBody>
                        <TableRow key='type'>
                            <TableCell align="right">Submission Type</TableCell>
                            <TableCell align="left"><b>{payload ? payload.component.replace("DST", "HEDIS") : null}</b></TableCell>
                        </TableRow>
                        <TableRow key='submissionIntention'>
                            <TableCell align="right">Submission Intention</TableCell>
                            <TableCell align="left"><b>{payload ? payload.submissionIntention : null}</b></TableCell>
                        </TableRow>
                        <TableRow key='specialArea'>
                            <TableCell align="right">Special Area</TableCell>
                            <TableCell align="left"><b>{payload ? payload.specialArea : null}</b></TableCell>
                        </TableRow>
                        <TableRow key='specialProject'>
                            <TableCell align="right">Special Project</TableCell>
                            <TableCell align="left"><b>{payload ? payload.specialProject : null}</b></TableCell>
                        </TableRow>
                        <TableRow key='publicReporting'>
                            <TableCell align="right">Public Reporting</TableCell>
                            <TableCell align="left"><b>{payload && payload.isPublicReported !== null ? (payload.isPublicReported === true ? 'Yes' : 'No') : 'Not Set'}</b></TableCell>
                        </TableRow>
                        {payload.productLine !== "Exchange" ?
                            <TableRow key='accreditationStatus'>
                                <TableCell align="right">Accreditation Status</TableCell>
                                <TableCell align="left"><b>{payload ? (payload.isAccredited ? 'Accredited' : 'Not Accredited') : 'Not Accredited'}</b></TableCell>
                            </TableRow> : null
                        }
                        <TableRow key='cmsContract'>
                            <TableCell align="right">CMS Contract</TableCell>
                            <TableCell align="left"><b>{payload ? payload.cmsContract : null}</b></TableCell>
                        </TableRow>
                        <TableRow key='reportingUnitId'>
                            <TableCell align="right">Reporting Unit Id</TableCell>
                            <TableCell align="left"><b>{payload ? payload.reportingUnitId : null}</b></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </CardContent>
        </Card >
    );
}

export function renderLoadingSkeleton(currentSubId: string, classes ) {
    return (
        <Grid className={classes.mt2 + " " + classes.mb2} container={true} spacing={2}>
            <Grid size={12}>
                <Grid container={true} spacing={2}>
                    <Grid>
                        <Typography className={classes.submissionTitle}>
                            Submission {currentSubId}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={12}>
                <Grid>
                    <Typography className={classes.submissionSubtitle}>
                        <Skeleton className={classes.skeleton50s} variant="rectangular" height={50}/>
                    </Typography>
                </Grid>
                <Grid>
                    <Typography className={classes.submissionSubtitle}>
                        <Skeleton className={classes.skeleton50} variant="rectangular" height={50}/>
                    </Typography>
                </Grid>
                <Grid>
                    <Typography className={classes.submissionSubtitle}>
                        <Skeleton className={classes.skeleton50} variant="rectangular" height={50}/>
                    </Typography>
                </Grid>
                <Grid>
                    <Typography className={classes.submissionSubtitle}>
                        <Skeleton className={classes.skeleton300} variant="rectangular" height={300}/>
                    </Typography>
                </Grid>
            </Grid>
        </Grid>)
}

export function renderTopDisplayGrid(
    payload: ISubmissionHomePayload,
    currentSubId: string,
    classes
) {
    return (
        <Grid className={classes.mt2 + " " + classes.mb2} container={true} spacing={2}>
            <Grid size={12}>
                <Grid container={true} spacing={2}>
                    <Grid >
                        <Typography className={classes.submissionTitle}>
                            Submission {currentSubId}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={12}>
                <Grid container={true} spacing={2}>
                    <Grid  size={'auto'}>  
                        <Link className={classes.submissionSubtitleLink}
                            to={"/availablesubmissionpage/all/all/" + payload.orgId}
                        >
                            {payload.orgName}
                        </Link>
                    </Grid>
                    <Grid className={classes.dividerPadding}>
                        <Divider orientation="vertical"/>
                    </Grid>
                    <Grid>
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <Link className={classes.submissionSubtitleLink}
                                            to={"/availablesubmissionpage/all/all/" + payload.orgId}
                                        >
                                            {payload.orgId}
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.submissionSubtitle1}>
                                        Org ID
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Grid>
                    <Grid className={classes.dividerPadding}>
                        <Divider orientation="vertical"/>
                    </Grid>
                    <Grid>
                        <table>
                            <tbody>
                                <tr>
                                    <td className={classes.submissionSubtitle}>
                                        {payload.productLine}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.submissionSubtitle1}>
                                        Product Line
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Grid>
                    <Grid className={classes.dividerPadding}>
                        <Divider orientation="vertical" />
                    </Grid>
                    <Grid>
                        <table>
                            <tbody>
                                <tr>
                                    <td className={classes.submissionSubtitle}>
                                        {payload.reportingProduct}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.submissionSubtitle1}>
                                        Reporting Product
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
